import React from "react";
import { useTranslation } from "react-i18next";
import { Card } from "./ui/Card";
import { Stack } from "./ui/Stack";
import { Heading } from "./ui/Heading";
import { Text } from "./ui/Text";
import { Button } from "./ui/Button";
import { Alert } from "./ui/Alert";

interface PaymentMethodOption {
  id:
    | "cash_on_delivery"
    | "cash_on_pickup"
    | "pay_by_invoice"
    | "online_payment";
  label: string;
  description: string;
  enabled: boolean;
  requiresEmailVerification?: boolean;
  isEmailVerified?: boolean;
}

interface CheckoutPaymentMethodProps {
  paymentMethods: {
    cashOnDelivery?: boolean;
    cashOnPickup?: boolean;
    payByInvoice?: boolean;
    onlinePayment?: boolean;
  };
  isOnlinePaymentEnabled: boolean;
  isEmailVerified?: boolean;
  selectedMethod:
    | "cash_on_delivery"
    | "cash_on_pickup"
    | "pay_by_invoice"
    | "online_payment"
    | null;
  onSelect: (
    method:
      | "cash_on_delivery"
      | "cash_on_pickup"
      | "pay_by_invoice"
      | "online_payment",
  ) => void;
  onNext: () => void;
  onBack: () => void;
  loading?: boolean;
  countryCode?: string;
  isDigitalOnlyOrder?: boolean; // Disable cash_on_pickup for digital orders
  isStripeConfigured?: boolean;
  isPayPalConfigured?: boolean;
}

export const CheckoutPaymentMethod: React.FC<CheckoutPaymentMethodProps> = ({
  paymentMethods,
  isOnlinePaymentEnabled,
  isEmailVerified = false,
  selectedMethod,
  onSelect,
  onNext,
  onBack,
  loading = false,
  countryCode,
  isDigitalOnlyOrder = false,
  isStripeConfigured = false,
  isPayPalConfigured = false,
}) => {
  const { t } = useTranslation();
  // Use only the provided payment methods (no defaults)
  const safePaymentMethods = {
    cashOnDelivery: paymentMethods?.cashOnDelivery ?? false,
    cashOnPickup: paymentMethods?.cashOnPickup ?? false,
    payByInvoice: paymentMethods?.payByInvoice ?? false,
    onlinePayment: paymentMethods?.onlinePayment ?? false,
  };

  // Check if any payment methods are configured
  const hasAnyPaymentMethod =
    safePaymentMethods.cashOnDelivery ||
    safePaymentMethods.cashOnPickup ||
    safePaymentMethods.payByInvoice ||
    safePaymentMethods.onlinePayment;

  const options: PaymentMethodOption[] = [
    {
      id: "cash_on_delivery" as const,
      label: t("checkout.cashOnDelivery"),
      description: t("checkout.cashOnDeliveryDesc"),
      enabled: safePaymentMethods.cashOnDelivery,
    },
    {
      id: "cash_on_pickup" as const,
      label: t("checkout.cashOnPickup"),
      description: t("checkout.cashOnPickupDesc"),
      enabled: safePaymentMethods.cashOnPickup && !isDigitalOnlyOrder, // Disable for digital orders
    },
    {
      id: "pay_by_invoice" as const,
      label: t("checkout.payByInvoice"),
      description: t("checkout.payByInvoiceDesc"),
      enabled: safePaymentMethods.payByInvoice,
      requiresEmailVerification: true,
      isEmailVerified,
    },
    // Show online payment option if either Stripe or PayPal is configured
    ...((isStripeConfigured || isPayPalConfigured) &&
    safePaymentMethods.onlinePayment &&
    isOnlinePaymentEnabled
      ? [
          {
            id: "online_payment" as const,
            label: t("checkout.onlinePayment") || "Online Payment",
            description: (() => {
              const gateways: string[] = [];
              if (isStripeConfigured) gateways.push("Credit Card");
              if (isPayPalConfigured) gateways.push("PayPal");
              return `Pay securely with ${gateways.join(" or ")}`;
            })(),
            enabled: true,
          },
        ]
      : []),
  ].filter((option) => option.enabled);

  // Only show enabled options (no fallback)
  const enabledOptions = options;

  const handleNext = () => {
    if (!selectedMethod) {
      return;
    }

    // Check if pay_by_invoice requires email verification
    if (selectedMethod === "pay_by_invoice" && !isEmailVerified) {
      return;
    }

    onNext();
  };

  const canProceed =
    hasAnyPaymentMethod &&
    selectedMethod &&
    (selectedMethod !== "pay_by_invoice" || isEmailVerified);
  return (
    <div className="max-w-2xl mx-auto">
      <Card padding="lg">
        <Stack spacing="md">
          <Heading size="lg">{t("checkout.selectPaymentMethod")}</Heading>
          <Text className="text-gray-600">
            {t("checkout.choosePaymentMethod")}
          </Text>

          {/* Warning message when no payment methods are configured */}
          {!hasAnyPaymentMethod && (
            <Alert variant="warning" className="mt-4">
              <Text size="sm" weight="semibold" className="mb-1">
                {t("checkout.noPaymentMethodsConfigured")}
              </Text>
              <Text size="sm">
                {countryCode
                  ? t("checkout.paymentMethodsNotConfiguredForCountry", {
                      countryCode,
                    })
                  : t("checkout.paymentMethodsNotConfigured")}
              </Text>
            </Alert>
          )}
          <div className="space-y-3 mt-4">
            {enabledOptions.length > 0 ? (
              enabledOptions.map((option) => {
                const isSelected = selectedMethod === option.id;
                const isDisabled =
                  option.requiresEmailVerification && !option.isEmailVerified;

                return (
                  <div
                    key={option.id}
                    onClick={() => !isDisabled && onSelect(option.id)}
                    className={`
          border-2 p-4 cursor-pointer transition-all
          ${isSelected ? "border-blue-500 bg-blue-50" : "border-gray-200 hover:border-gray-300"}
          ${isDisabled ? "opacity-50 cursor-not-allowed" : ""}
         `}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <input
                            type="radio"
                            name="paymentMethod"
                            id={option.id}
                            checked={isSelected}
                            onChange={() => !isDisabled && onSelect(option.id)}
                            disabled={isDisabled}
                            className="w-4 h-4 text-blue-600"
                          />
                          <label
                            htmlFor={option.id}
                            className="cursor-pointer flex-1"
                          >
                            <Text weight="semibold" className="mb-1">
                              {option.label}
                            </Text>
                            <Text size="sm" className="text-gray-600">
                              {option.description}
                            </Text>
                            {/* Show available gateways for online payment */}
                            {option.id === "online_payment" &&
                              (isStripeConfigured || isPayPalConfigured) && (
                                <div className="mt-2 flex flex-wrap gap-2">
                                  {isStripeConfigured && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                      💳 Credit Card
                                    </span>
                                  )}
                                  {isPayPalConfigured && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                                      🅿️ PayPal
                                    </span>
                                  )}
                                </div>
                              )}
                          </label>
                        </div>
                        {option.requiresEmailVerification &&
                          !option.isEmailVerified && (
                            <Alert variant="warning" className="mt-2">
                              <Text size="sm">
                                {t("checkout.emailVerificationRequired")}
                              </Text>
                            </Alert>
                          )}
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <Alert variant="warning" className="mt-4">
                <Text size="sm">{t("checkout.noPaymentMethodsEnabled")}</Text>
              </Alert>
            )}
          </div>

          {selectedMethod === "pay_by_invoice" && !isEmailVerified && (
            <Alert variant="error" className="mt-4">
              <Text size="sm">{t("checkout.mustVerifyEmail")}</Text>
            </Alert>
          )}

          <Stack spacing="sm" className="mt-6">
            <Button
              onClick={handleNext}
              disabled={!canProceed || loading}
              loading={loading}
              className="w-full"
            >
              {t("checkout.continue")}
            </Button>
            <Button
              variant="outline"
              onClick={onBack}
              className="w-full"
              disabled={loading}
            >
              {t("checkout.back")}
            </Button>
          </Stack>
        </Stack>
      </Card>
    </div>
  );
};
